import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { maxInt } from '../../../modals/licences/services/licenceService';
import { LicenseStatusEnum } from '../../../../core/domainModels/licences/LicenseStatus';
import {
    assignColDefsDefaults,
    COLUMN_WIDTH,
    compareDateFilter,
    dateSortComparator,
    fieldName,
    filterMenuTab,
} from '../../../../core/helpers/aggrid';
import { TachoColumnState } from '../../../../core/domainModels/displayPreferences/tachoColumnState';
import { ColumnTooltip } from '../../../components/TachoTooltip/columnTooltip';
import { Client } from '../../../../core/domainModels/clients/Client';
import { LicenseModel } from '../../../../core/domainModels/licences/LicenseModel';
import { LicenseVersion } from '../../../../core/domainModels/licences/LicenseVersion';
import { LicenseTachoModule } from '../../../../core/domainModels/modules/LicenseTachoModule';
import { formatToYesOrNo } from '../../../components/AgGrid/services';
import { dateToStringFormat } from '../../../../core/helpers/moment';

export const licencesTableDefaultColumnsDefs: ColDef = {
    sortable: true,
    resizable: true,
    tooltipComponent: ColumnTooltip,
    sortingOrder: ['desc', 'asc', null],
    menuTabs: filterMenuTab,
    cellClassRules: {
        isActive: (params: { data: { status: LicenseStatusEnum } }) => {
            return params.data.status === LicenseStatusEnum.Blocked;
        },
        isUpdate: params => {
            return !params.data.friendlyLicenseNumber;
        },
    },
};

export const licencesTableColumnsDefinitions = (
    formatMessage: ({ id }: { id: string }) => string,
    clients: Array<Client>,
    defColumnState: Array<TachoColumnState>,
): Array<ColDef> => {
    const columns: Array<ColDef> = [
        {
            colId: fieldName<LicenseModel>('id'),
            headerName: formatMessage({ id: 'licencesNumber' }),
            field: fieldName<LicenseModel>('id'),
            filterValueGetter(params) {
                return params.data.friendlyLicenseNumber.toString();
            },
            valueGetter(params) {
                return params.data.friendlyLicenseNumber.toString();
            },
            resizable: false,
            width: 120,
        },
        {
            colId: fieldName<Client>('name'),
            field: fieldName<Client>('name'),
            headerName: formatMessage({ id: 'clientName' }),
            headerTooltip: formatMessage({ id: 'clientName' }),
            initialWidth: COLUMN_WIDTH.XL,
            initialSort: 'asc',
        },
        {
            colId: fieldName<LicenseVersion>('paymentStatus'),
            field: fieldName<LicenseVersion>('paymentStatus'),
            headerName: formatMessage({ id: 'licencesPaymentStatus' }),
            initialWidth: 130,
            valueFormatter: (params: any) => {
                return params.value ? formatMessage({ id: params.value }) : '-';
            },
            filterValueGetter: params => {
                return params.data.paymentStatus ? formatMessage({ id: params.data.paymentStatus }) : '-';
            },
        },
        {
            colId: fieldName<LicenseVersion>('type'),
            field: fieldName<LicenseVersion>('type'),
            headerName: formatMessage({ id: 'licencesLicenseType' }),
            initialWidth: 130,
            valueFormatter: (params: any) => {
                return params.value ? formatMessage({ id: params.value }) : '-';
            },
            filterValueGetter: params => {
                return params.data.type ? formatMessage({ id: params.data.type }) : '-';
            },
        },
        {
            colId: fieldName<LicenseVersion>('validFrom'),
            field: fieldName<LicenseVersion>('validFrom'),
            headerName: formatMessage({ id: 'licencesValidFrom' }),
            initialWidth: COLUMN_WIDTH.SM,
            comparator: dateSortComparator,
            valueFormatter: (params: any) => {
                return dateToStringFormat(params.value);
            },
            filterValueGetter: params => {
                return dateToStringFormat(params.data.validFrom);
            },
            filterParams: {
                comparator: compareDateFilter,
            },
        },
        {
            colId: fieldName<LicenseVersion>('validTo'),
            field: fieldName<LicenseVersion>('validTo'),
            headerName: formatMessage({ id: 'licencesValidTo' }),
            initialWidth: COLUMN_WIDTH.SM,
            comparator: dateSortComparator,
            valueFormatter: (params: any) => {
                return dateToStringFormat(params.value);
            },
            filterValueGetter: params => {
                return dateToStringFormat(params.data.validTo);
            },
            filterParams: {
                comparator: compareDateFilter,
            },
        },

        {
            colId: fieldName<LicenseVersion>('paymentDeadline'),
            field: fieldName<LicenseVersion>('paymentDeadline'),
            headerName: formatMessage({ id: 'licencesPaymentDeadline' }),
            initialWidth: COLUMN_WIDTH.SM,
            comparator: dateSortComparator,
            valueFormatter: (params: any) => {
                return dateToStringFormat(params.value);
            },
            filterValueGetter: params => {
                return dateToStringFormat(params.data.paymentDeadline);
            },
            filterParams: {
                comparator: compareDateFilter,
            },
        },
        {
            colId: fieldName<LicenseVersion>('maximumNumberOfSupportedCars'),
            field: fieldName<LicenseVersion>('maximumNumberOfSupportedCars'),
            headerName: formatMessage({ id: 'licencesNumberOfCars' }),
            initialWidth: COLUMN_WIDTH.SM,
            valueFormatter(params) {
                return params.value === maxInt ? formatMessage({ id: 'licenceCarsFull' }) : params.value;
            },
            filterValueGetter(params) {
                return params.data.maximumNumberOfSupportedCars === maxInt
                    ? formatMessage({ id: 'licenceCarsFull' })
                    : params.data.maximumNumberOfSupportedCars.toString();
            },
        },
        {
            colId: fieldName<LicenseVersion>('allowedModules'),
            field: fieldName<LicenseVersion>('allowedModules'),
            headerName: formatMessage({ id: 'licencesAllowedModules' }),
            initialWidth: 360,
            valueFormatter: (params: any) => {
                if (params.value) {
                    const mapped = params.value.map((item: LicenseTachoModule) => {
                        return `${formatMessage({ id: item.tachoModuleId })} : ${item.maximumLoggedUsers}`;
                    });
                    return mapped.join(', ');
                }
            },
            filterValueGetter: params => {
                return (
                    params.data.allowedModules &&
                    params.data.allowedModules
                        .map((item: LicenseTachoModule) => {
                            return `${formatMessage({ id: item.tachoModuleId })}`;
                        })
                        .join(', ')
                );
            },
        },
        {
            colId: fieldName<LicenseModel>('isServiceEdition'),
            headerName: formatMessage({ id: 'licencesIsServiceEdition' }),
            headerTooltip: formatMessage({ id: 'licencesIsServiceEdition' }),
            field: fieldName<LicenseModel>('isServiceEdition'),
            initialWidth: COLUMN_WIDTH.XS,
            valueGetter: params => formatToYesOrNo(params.data?.isServiceEdition, formatMessage),
            filterValueGetter: params => formatToYesOrNo(params.data?.isServiceEdition, formatMessage),
        },
        {
            colId: fieldName<Client>('nip'),
            field: fieldName<Client>('nip'),
            headerName: formatMessage({ id: 'clientNip' }),
            headerTooltip: formatMessage({ id: 'clientNip' }),
            initialWidth: 105,
            filterValueGetter: cellParams => clients.find(item => cellParams.data.clientId === item.id)?.nip || '',
        },

        {
            colId: fieldName<LicenseModel>('licenseVersions'),
            field: fieldName<LicenseModel>('licenseVersions'),
            headerName: formatMessage({ id: 'licencesVersionCount' }),
            headerTooltip: formatMessage({ id: 'licencesVersionCount' }),
            initialWidth: COLUMN_WIDTH.SM,
            valueFormatter: (cellParams: ValueFormatterParams) => cellParams.value?.length,
            filterValueGetter: params => {
                return params.data.licenseVersions?.length;
            },
        },
        {
            colId: fieldName<LicenseModel>('tokenVersion'),
            field: fieldName<LicenseModel>('tokenVersion'),
            headerName: formatMessage({ id: 'licencesTokenVersion' }),
            headerTooltip: formatMessage({ id: 'licencesTokenVersion' }),
            initialWidth: COLUMN_WIDTH.MD,
            filterValueGetter: params => {
                return params.data.licenseVersions?.length;
            },
        },
        {
            colId: fieldName<LicenseModel>('tokenValidFrom'),
            field: fieldName<LicenseModel>('tokenValidFrom'),
            headerName: formatMessage({ id: 'licencesTokenValidFrom' }),
            initialWidth: COLUMN_WIDTH.SM,
            comparator: dateSortComparator,
            valueFormatter: (params: any) => {
                return dateToStringFormat(params.value);
            },
            filterValueGetter: params => {
                return dateToStringFormat(params.data.tokenValidFrom);
            },
            filterParams: {
                comparator: compareDateFilter,
            },
        },
        {
            colId: fieldName<LicenseModel>('notes'),
            field: fieldName<LicenseModel>('notes'),
            headerName: formatMessage({ id: 'clientNotes' }),
            headerTooltip: formatMessage({ id: 'clientNotes' }),
            initialWidth: 140,
            flex: 1,
        },
    ];
    assignColDefsDefaults(columns, defColumnState, true);
    return columns;
};
